import {
  getLocationManagedByUser,
  getLocationManagersByLocationId,
  getLocationsManagerOffices,
  getLocationImages,
  editOfficeApi,
  removeOfficeApi,
  createOfficeApi,
  addManyLocationManagers,
} from '../../api/api'
import { createAsyncAction, FULFILLED, PENDING, REJECTED, createAction } from '../utils'
// ------------------------------------
// Action Types & Creators
// ------------------------------------

export const LOCATION_USER_MANAGES_SET = 'foodsby/locationManager/LOCATION_USER_MANAGES_SET'
export const LOCATION_MANAGERS_SET = 'foodsby/locationManager/LOCATION_MANAGERS_SET'
export const LOCATION_MANAGER_OFFICES_SET = 'foodsby/locationManager/LOCATION_MANAGER_OFFICES_SET'
export const LOCATION_IMAGES_SET = 'foodsby/locationManager/LOCATION_IMAGES_SET'
export const EDIT_OFFICE = 'foodsby/locationManager/EDIT_OFFICE'
export const REMOVE_OFFICE = 'foodsby/locationManager/REMOVE_OFFICE'
export const PAGE_OFFICES_SET = 'foodsby/locationManager/PAGE_OFFICES_SET'
export const CREATE_OFFICE = 'foodsby/locationManager/CREATE_OFFICE'
export const CREATE_LOCATION_MANAGER = 'foodsby/locationManager/CREATE_LOCATION_MANAGER'
export const CLEAR_OFFICE_ERRORS_SET = 'foodsby/locationManager/CLEAR_OFFICE_ERRORS'

export const setLocationUserManages = createAsyncAction(LOCATION_USER_MANAGES_SET)
export const setLocationManagers = createAsyncAction(LOCATION_MANAGERS_SET)
export const setLocationManagerOffices = createAsyncAction(LOCATION_MANAGER_OFFICES_SET)
export const setLocationImages = createAsyncAction(LOCATION_IMAGES_SET)
export const editOffice = createAsyncAction(EDIT_OFFICE)
export const removeOffice = createAsyncAction(REMOVE_OFFICE)
export const setPageOffices = createAction(PAGE_OFFICES_SET)
export const createOffice = createAsyncAction(CREATE_OFFICE)
export const createManyLocationManagers = createAsyncAction(CREATE_LOCATION_MANAGER)
export const clearOfficeErrors = createAction(CLEAR_OFFICE_ERRORS_SET)

export const loadLocationUserManagesStart = locationId => {
  return dispatch => {
    dispatch(setLocationUserManages(getLocationManagedByUser(locationId)))
  }
}

export const loadLocationManagersStart = locationId => {
  return dispatch => {
    dispatch(setLocationManagers(getLocationManagersByLocationId(locationId)))
  }
}

export const loadLocationManagerOfficesStart = locationId => {
  return dispatch => {
    dispatch(setLocationManagerOffices(getLocationsManagerOffices(locationId)))
  }
}

export const loadLocationImagesStart = locationId => {
  return dispatch => {
    dispatch(setLocationImages(getLocationImages(locationId)))
  }
}

export const editOfficeStart = (officeId, locationId, payload) => {
  return async dispatch => {
    await dispatch(editOffice(editOfficeApi(officeId, payload)))
    dispatch(loadLocationManagerOfficesStart(locationId))
  }
}

export const removeOfficeStart = (officeId, locationId) => {
  return async dispatch => {
    await dispatch(removeOffice(removeOfficeApi(officeId)))
    dispatch(loadLocationManagerOfficesStart(locationId))
  }
}

export const createOfficeStart = (payload, locationId) => {
  return async dispatch => {
    await dispatch(createOffice(createOfficeApi(payload, locationId)))
    dispatch(loadLocationManagerOfficesStart(locationId))
  }
}

export const createManyLocationManagersStart = (locationId, payload) => {
  return async dispatch => {
    await dispatch(createManyLocationManagers(addManyLocationManagers(locationId, payload)))
    await dispatch(loadLocationManagersStart(locationId))
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------

const ACTION_HANDLERS = {
  [REJECTED(LOCATION_USER_MANAGES_SET)]: (state, action) => {
    return {
      ...state,
      isLoadingLocationUserManages: false,
      errorLoadingLocationUserManages: action.payload ?? null,
      locationUserManages: null,
      locationUserManagesInitialized: true,
    }
  },
  [PENDING(LOCATION_USER_MANAGES_SET)]: state => {
    return {
      ...state,
      isLoadingLocationUserManages: true,
    }
  },
  [FULFILLED(LOCATION_USER_MANAGES_SET)]: (state, action) => {
    return {
      ...state,
      locationUserManages: action.payload,
      isLoadingLocationUserManages: false,
      errorLoadingLocationUserManages: undefined,
      locationUserManagesInitialized: true,
    }
  },
  [REJECTED(LOCATION_MANAGERS_SET)]: (state, action) => {
    return {
      ...state,
      isLoadingLocationManagers: false,
      errorLoadingLocationManagers: action.payload ?? null,
      locationManagers: [],
    }
  },
  [PENDING(LOCATION_MANAGERS_SET)]: state => {
    return {
      ...state,
      isLoadingLocationManagers: true,
    }
  },
  [FULFILLED(LOCATION_MANAGERS_SET)]: (state, action) => {
    return {
      ...state,
      locationManagers: action.payload ? action.payload : [],
      isLoadingLocationManagers: false,
      errorLoadingLocationManagers: undefined,
    }
  },
  [REJECTED(LOCATION_MANAGER_OFFICES_SET)]: (state, action) => {
    return {
      ...state,
      isLoadingLocationOffices: false,
      errorLoadingLocationOffices: action.payload ?? null,
      locationOffices: [],
    }
  },
  [PENDING(LOCATION_MANAGER_OFFICES_SET)]: state => {
    return {
      ...state,
      isLoadingLocationOffices: true,
    }
  },
  [FULFILLED(LOCATION_MANAGER_OFFICES_SET)]: (state, action) => {
    return {
      ...state,
      locationOffices: action.payload ? action.payload.content : [],
      isLoadingLocationOffices: false,
      errorLoadingLocationOffices: undefined,
    }
  },
  [REJECTED(LOCATION_IMAGES_SET)]: (state, action) => {
    return {
      ...state,
      isLoadingLocationImages: false,
      errorLoadingLocationImages: action.payload ?? null,
      locationImages: [],
    }
  },
  [PENDING(LOCATION_IMAGES_SET)]: state => {
    return {
      ...state,
      isLoadingLocationImages: true,
    }
  },
  [FULFILLED(LOCATION_IMAGES_SET)]: (state, action) => {
    return {
      ...state,
      locationImages: action.payload ? action.payload.images : [],
      isLoadingLocationImages: false,
      errorLoadingLocationImages: undefined,
    }
  },
  [PENDING(EDIT_OFFICE)]: state => {
    return {
      ...state,
      isEditingOffice: true,
      errorEditingOffice: undefined,
    }
  },
  [FULFILLED(EDIT_OFFICE)]: state => {
    return {
      ...state,
      isEditingOffice: false,
      errorEditingOffice: undefined,
    }
  },
  [REJECTED(EDIT_OFFICE)]: state => {
    return {
      ...state,
      isEditingOffice: false,
      errorEditingOffice: 'Unable to edit office, please try again later or contact support.',
    }
  },
  [PENDING(REMOVE_OFFICE)]: state => {
    return {
      ...state,
      isRemovingOffice: true,
      errorRemovingOffice: undefined,
    }
  },
  [FULFILLED(REMOVE_OFFICE)]: state => {
    return {
      ...state,
      isRemovingOffice: false,
      errorRemovingOffice: undefined,
    }
  },
  [REJECTED(REMOVE_OFFICE)]: state => {
    return {
      ...state,
      isRemovingOffice: false,
      errorRemovingOffice: 'Unable to remove office, please try again later or contact support.',
    }
  },
  [PENDING(CREATE_OFFICE)]: state => {
    return {
      ...state,
      isCreatingOffice: true,
      errorCreatingOffice: undefined,
    }
  },
  [FULFILLED(CREATE_OFFICE)]: state => {
    return {
      ...state,
      isCreatingOffice: false,
      errorCreatingOffice: undefined,
    }
  },
  [REJECTED(CREATE_OFFICE)]: (state, action) => {
    return {
      ...state,
      isCreatingOffice: false,
      errorCreatingOffice:
        action.payload.code === 409
          ? 'There is already an office with the same domain at your location. Please update that office, or contact support for any questions.'
          : 'Unable to create office, please try again later or contact support.',
    }
  },
  [PENDING(CREATE_LOCATION_MANAGER)]: state => {
    return {
      ...state,
      isCreatingManyLocationManagers: true,
      errorCreatingManyLocationManagers: undefined,
    }
  },
  [FULFILLED(CREATE_LOCATION_MANAGER)]: state => {
    return {
      ...state,
      isCreatingManyLocationManagers: false,
      errorCreatingManyLocationManagers: undefined,
    }
  },
  [REJECTED(CREATE_LOCATION_MANAGER)]: (state, action) => {
    return {
      ...state,
      isCreatingManyLocationManagers: false,
      errorCreatingManyLocationManagers: action.payload.message,
    }
  },
  [PAGE_OFFICES_SET]: (state, action) => {
    return {
      ...state,
      pageOffices: action.payload,
    }
  },
  [CLEAR_OFFICE_ERRORS_SET]: state => {
    return {
      ...state,
      errorCreatingOffice: false,
      errorEditingOffice: false,
      errorRemovingOffice: false,
    }
  },
}

// ------------------------------------
// Reducer
// ------------------------------------
export const initialState = {
  locationUserManages: null,
  locationManagers: [],
  isLoadingLocationManagers: false,
  isLoadingLocationUserManages: false,
  errorLoadingLocationUserManages: undefined,
  errorLoadingLocationManagers: undefined,
  locationOffices: [],
  isLoadingLocationOffices: false,
  errorLoadingLocationOffices: undefined,
  locationUserManagesInitialized: false,
  locationImages: [],
  isLoadingLocationImages: false,
  errorLoadingLocationImages: undefined,
  isEditingOffice: false,
  errorEditingOffice: undefined,
  isRemovingOffice: false,
  errorRemovingOffice: undefined,
  pageOffices: 0,
  isCreatingOffice: false,
  errorCreatingOffice: undefined,
  isCreatingManyLocationManagers: false,
  errorCreatingManyLocationManagers: undefined,
}

export default function locationManager(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}
